import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useMetricsContext } from '../context/metrics';
import { FORGE_UI_ANALYTICS_CHANNEL } from '.';
import { useMemoOne } from 'use-memo-one';
import { fg } from '@atlaskit/platform-feature-flags';
import { createSingleEventPerAppFilter } from './eventFilters';
import type { FeatureFlagEnv, FeatureFlagEventTypes } from '../custom-ui/bridge/types';

type EnvironmentType = 'PRODUCTION' | 'DEVELOPMENT' | 'STAGING';

type Components = Record<
	string,
	{
		count: number;
		propCount: Record<string, number>;
	}
>;

type BaseEventPayload = {
	source: string;
};

type TrackComponentUsageRenderedPayload = BaseEventPayload & {
	components: Components;
	forgeEnvironment: EnvironmentType;
	forgeReactMajorVersion: string | null;
	isLegacyUiKit: boolean;
};

type TrackRendererRenderedPayload = BaseEventPayload & {
	target: 'renderer'; // 'useWebRuntime' is used for the UI Kit 1, and should deprecated in the future clean up
};

type TrackBridgePayload = BaseEventPayload & {
	additionalAttributes?: Record<string, unknown>;
	forgeEnvironment?: EnvironmentType;
	method: string;
};

// For extensibility
type TrackBridgeSucceedPayload = TrackBridgePayload;

type TrackBridgeFailedPayload = TrackBridgePayload & {
	errorName: string;
};

type TrackExtensionLoadedPayload = BaseEventPayload & {
	extensionType: 'extension' | 'inlineExtension' | 'bodiedExtension';
	forgeEnvironment?: EnvironmentType;
	// Used for tracking custom macro config loading
	isConfiguring?: boolean;
	isInserting?: boolean;
	renderType: 'CustomUI' | 'UIKit1' | 'UIKit';
};

type TrackExtensionRemountedPayload = BaseEventPayload & {
	mountCount: number;
};

type FeatureFlagEventActionType =
	| FeatureFlagEventTypes.INITIALIZATION
	| FeatureFlagEventTypes.CHECKFLAG;

type FireEventPayload = {
	action:
		| 'succeeded'
		| 'failed'
		| 'loaded'
		| 'rendered'
		| 'remounted'
		| 'resized'
		| 'clicked'
		| 'performance.profiled'
		| 'contextTokenComparison'
		| FeatureFlagEventActionType;
	actionSubject:
		| 'forge.ui.bridge'
		| 'forge.ui.analytics.component_usage'
		| 'forge.ui.extension'
		| 'forge.ui.renderer'
		| 'forge.ui.invocation'
		| 'forge.ui.feature_flag';
	attributes?: Record<string, unknown>;
	source: string;
};

export const fireEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ action, actionSubject, source, attributes }: FireEventPayload,
	options?: { filter?: (event: UIAnalyticsEvent) => boolean; sampleRate?: number },
): void => {
	const { sampleRate, filter } = options ?? {};
	// If a sample rate is provided, we can use it to determine whether to fire the event
	if (sampleRate) {
		const randomValue = Math.random();
		if (randomValue > sampleRate) {
			return; // Don't fire the event
		}
	}

	const event = createAnalyticsEvent({
		eventType: OPERATIONAL_EVENT_TYPE,
		data: {
			action,
			actionSubject,
			attributes,
			source,
			tags: ['forge'],
		},
	});

	if (filter && !filter(event)) {
		return; // Don't fire the event if it doesn't pass the filter
	}
	event.fire(FORGE_UI_ANALYTICS_CHANNEL);
};

// Ideally this should be removed entirely in the future.
// see: https://hello.atlassian.net/wiki/spaces/ECO/pages/5687458412/Why+is+GASv3+Event+Costs+and+Volumes+Climbing+Cost+Saving+Proposal
const legacyTrackBridgeSucceed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, method, additionalAttributes, forgeEnvironment }: TrackBridgeSucceedPayload,
) =>
	fireEvent(createAnalyticsEvent, {
		action: 'succeeded',
		actionSubject: 'forge.ui.bridge',
		source,
		attributes: {
			...(additionalAttributes ?? {}),
			method,
			forgeEnvironment,
		},
	});

const optimisedTrackBridgeSucceed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, method, additionalAttributes, forgeEnvironment }: TrackBridgeSucceedPayload,
) => {
	// currently only `navigate` bridge method is used in the product dashboard
	if (method === 'navigate') {
		// send only 10% of the events as sampled data should sufficient to infer the frequence of feature usage.
		fireEvent(
			createAnalyticsEvent,
			{
				action: 'succeeded',
				actionSubject: 'forge.ui.bridge',
				source,
				attributes: {
					...(additionalAttributes ?? {}),
					method,
					forgeEnvironment,
				},
			},
			{ sampleRate: 0.1 },
		);
	}
};

const trackForgeFeatureFlagEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		appType,
		baseUrl,
		appId,
		flagName,
		eventType,
		success,
		featureFlagEnv,
		forgeEnvironment,
	}: TrackForgeFeatureFlagEventPayload,
) => {
	fireEvent(createAnalyticsEvent, {
		action: eventType,
		actionSubject: 'forge.ui.feature_flag',
		source,
		attributes: {
			appType,
			baseUrl,
			appId,
			flagName,
			eventType,
			success,
			featureFlagEnv,
			forgeEnvironment,
		},
	});
};

export const trackBridgeSucceed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	payload: TrackBridgeSucceedPayload,
): void =>
	fg('forge-ui-event-cost-optimisation')
		? optimisedTrackBridgeSucceed(createAnalyticsEvent, payload)
		: legacyTrackBridgeSucceed(createAnalyticsEvent, payload);

export const trackBridgeFailed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, errorName, method, forgeEnvironment, additionalAttributes }: TrackBridgeFailedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'failed',
		actionSubject: 'forge.ui.bridge',
		source,
		attributes: {
			...(additionalAttributes ?? {}),
			error: errorName,
			method,
			forgeEnvironment,
		},
	});

export const trackComponentUsageRendered = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		isLegacyUiKit,
		forgeEnvironment,
		components,
		forgeReactMajorVersion,
	}: TrackComponentUsageRenderedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'rendered',
		actionSubject: 'forge.ui.analytics.component_usage',
		source,
		attributes: {
			appType: `UIKit${isLegacyUiKit ? '1' : ''}`,
			forgeEnvironment,
			components,
			forgeReactMajorVersion,
		},
	});

export const trackRendererRendered = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, target }: TrackRendererRenderedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'rendered',
		actionSubject: 'forge.ui.renderer',
		source,
		attributes: {
			target,
		},
	});

const extensionLoadedEventFilter = createSingleEventPerAppFilter();
const trackExtensionLoaded = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		forgeEnvironment,
		renderType,
		extensionType,
		isConfiguring,
		isInserting,
	}: TrackExtensionLoadedPayload,
) => {
	const options = fg('forge-ui-event-cost-optimisation')
		? { filter: extensionLoadedEventFilter }
		: undefined;
	fireEvent(
		createAnalyticsEvent,
		{
			action: 'loaded',
			actionSubject: 'forge.ui.extension',
			source,
			attributes: {
				forgeEnvironment,
				renderType,
				extensionType,
				isConfiguring,
				isInserting,
			},
		},
		options,
	);
};

const trackExtensionRemounted = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, mountCount }: TrackExtensionRemountedPayload,
) =>
	fireEvent(createAnalyticsEvent, {
		action: 'remounted',
		actionSubject: 'forge.ui.extension',
		source,
		attributes: {
			mountCount,
		},
	});

type TrackExtensionResizedPayload = BaseEventPayload & {
	appType: 'CustomUI' | 'UIKit';
	extensionType?: string;
	forgeEnvironment?: EnvironmentType;
	navigationTimeStart: number;
	resizedValues: Array<{ elapsed: number; height: number }>;
	resizeTotal: number;
};

const trackExtensionResized = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		resizeTotal,
		appType,
		extensionType,
		navigationTimeStart,
		resizedValues,
		forgeEnvironment,
	}: TrackExtensionResizedPayload,
) =>
	fireEvent(createAnalyticsEvent, {
		action: 'resized',
		actionSubject: 'forge.ui.extension',
		source,
		attributes: {
			extensionType,
			resizeTotal,
			appType,
			navigationTimeStart,
			resizedValues,
			forgeEnvironment,
		},
	});

type TrackExtensionClickedPayload = BaseEventPayload & {
	appType: 'CustomUI' | 'UIKit';
	baseUrl: string;
	componentName?: string;
	extensionType: string;
	forgeEnvironment?: EnvironmentType;
};

type TrackForgeFeatureFlagEventPayload = BaseEventPayload & {
	appId: string;
	appType: 'CustomUI' | 'UIKit';
	baseUrl: string;
	eventType: FeatureFlagEventTypes;
	featureFlagEnv: FeatureFlagEnv;
	flagName?: string;
	forgeEnvironment?: EnvironmentType;
	success: boolean;
};

const trackExtensionClicked = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		appType,
		extensionType,
		forgeEnvironment,
		baseUrl,
		componentName: component,
	}: TrackExtensionClickedPayload,
) =>
	fireEvent(createAnalyticsEvent, {
		action: 'clicked',
		actionSubject: 'forge.ui.extension',
		source,
		attributes: {
			extensionType,
			appType,
			forgeEnvironment,
			baseUrl,
			component,
		},
	});

const createForgeUiAnalyticsEventTrackers = ({
	createAnalyticsEvent,
	source,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	source: string;
}) => {
	return {
		trackComponentUsageRendered: ({
			isLegacyUiKit,
			forgeEnvironment,
			components,
			forgeReactMajorVersion,
		}: Omit<TrackComponentUsageRenderedPayload, 'source'>) =>
			trackComponentUsageRendered(createAnalyticsEvent, {
				source,
				isLegacyUiKit,
				forgeEnvironment,
				components,
				forgeReactMajorVersion,
			}),
		trackRendererRendered: ({ target }: Omit<TrackRendererRenderedPayload, 'source'>) =>
			trackRendererRendered(createAnalyticsEvent, { source, target }),
		trackExtensionLoaded: ({
			forgeEnvironment,
			renderType,
			extensionType,
			isConfiguring,
			isInserting,
		}: Omit<TrackExtensionLoadedPayload, 'source'>) =>
			trackExtensionLoaded(createAnalyticsEvent, {
				source,
				forgeEnvironment,
				renderType,
				extensionType,
				isConfiguring,
				isInserting,
			}),
		trackExtensionRemounted: ({ mountCount }: Omit<TrackExtensionRemountedPayload, 'source'>) =>
			trackExtensionRemounted(createAnalyticsEvent, { source, mountCount }),
		trackBridgeSucceed: ({ method, forgeEnvironment }: Omit<TrackBridgeSucceedPayload, 'source'>) =>
			trackBridgeSucceed(createAnalyticsEvent, { source, method, forgeEnvironment }),
		trackBridgeFailed: ({
			method,
			errorName,
			forgeEnvironment,
		}: Omit<TrackBridgeFailedPayload, 'source'>) =>
			trackBridgeFailed(createAnalyticsEvent, { source, errorName, method, forgeEnvironment }),
		trackExtensionResized: ({
			navigationTimeStart,
			resizedValues,
			resizeTotal,
			appType,
			extensionType,
			forgeEnvironment,
		}: Omit<TrackExtensionResizedPayload, 'source'>) =>
			trackExtensionResized(createAnalyticsEvent, {
				source,
				navigationTimeStart,
				resizeTotal,
				resizedValues,
				appType,
				extensionType,
				forgeEnvironment,
			}),
		trackExtensionClicked: ({
			extensionType,
			appType,
			forgeEnvironment,
			baseUrl,
			componentName,
		}: Omit<TrackExtensionClickedPayload, 'source'>) =>
			trackExtensionClicked(createAnalyticsEvent, {
				source,
				extensionType,
				appType,
				forgeEnvironment,
				baseUrl,
				componentName,
			}),
		trackForgeFeatureFlagEvent: ({
			appType,
			baseUrl,
			appId,
			flagName,
			eventType,
			success,
			featureFlagEnv,
			forgeEnvironment,
		}: Omit<TrackForgeFeatureFlagEventPayload, 'source'>) =>
			trackForgeFeatureFlagEvent(createAnalyticsEvent, {
				source,
				appType,
				baseUrl,
				appId,
				flagName,
				eventType,
				success,
				featureFlagEnv,
				forgeEnvironment,
			}),
	};
};

export const useForgeUiAnalyticsEvent = (): {
	trackBridgeFailed: ({
		method,
		errorName,
		forgeEnvironment,
	}: Omit<TrackBridgeFailedPayload, 'source'>) => void;
	trackBridgeSucceed: ({
		method,
		forgeEnvironment,
	}: Omit<TrackBridgeSucceedPayload, 'source'>) => void;
	trackComponentUsageRendered: ({
		isLegacyUiKit,
		forgeEnvironment,
		components,
		forgeReactMajorVersion,
	}: Omit<TrackComponentUsageRenderedPayload, 'source'>) => void;
	trackExtensionClicked: ({
		extensionType,
		appType,
		forgeEnvironment,
		baseUrl,
		componentName,
	}: Omit<TrackExtensionClickedPayload, 'source'>) => void;
	trackExtensionLoaded: ({
		forgeEnvironment,
		renderType,
		extensionType,
		isConfiguring,
		isInserting,
	}: Omit<TrackExtensionLoadedPayload, 'source'>) => void;
	trackExtensionRemounted: ({ mountCount }: Omit<TrackExtensionRemountedPayload, 'source'>) => void;
	trackExtensionResized: ({
		navigationTimeStart,
		resizedValues,
		resizeTotal,
		appType,
		extensionType,
		forgeEnvironment,
	}: Omit<TrackExtensionResizedPayload, 'source'>) => void;
	trackForgeFeatureFlagEvent: ({
		appType,
		baseUrl,
		appId,
		flagName,
		eventType,
		success,
		featureFlagEnv,
		forgeEnvironment,
	}: Omit<TrackForgeFeatureFlagEventPayload, 'source'>) => void;
	trackRendererRendered: ({ target }: Omit<TrackRendererRenderedPayload, 'source'>) => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { page: source } = useMetricsContext();

	const cachedTrackers = useMemoOne(
		() => createForgeUiAnalyticsEventTrackers({ createAnalyticsEvent, source }),
		[createAnalyticsEvent, source],
	);

	return fg('forge-ui-macro-config-update-improvement')
		? cachedTrackers
		: createForgeUiAnalyticsEventTrackers({ createAnalyticsEvent, source });
};
